<template>
    <div class="flex flex-col items-end gap-1.5">
        <div class="max-w-[80%] whitespace-pre-wrap rounded-2xl bg-slate-900 px-4 py-2 text-sm text-white shadow-sm">
            <template v-for="(segment, idx) in segments" :key="idx">
                <BasePopover
                    v-if="segment.tag"
                    as-child
                    side="top"
                    :side-offset="6"
                    :collision-padding="12"
                    content-class="z-50 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
                    <template #trigger>
                        <button
                            type="button"
                            class="rounded-md bg-white/15 px-1 py-0.5 underline decoration-dotted underline-offset-2 hover:bg-white/25"
                            data-testid="user-bubble-tag">
                            {{ segment.text }}
                        </button>
                    </template>
                    <template #content>
                        <div class="flex flex-col gap-2 text-slate-900">
                            <div class="flex items-start gap-3">
                                <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E0E9FF] text-xs font-medium text-slate-700" aria-hidden="true">
                                    {{ initials(segment.tag.name) }}
                                </div>
                                <div class="flex flex-col gap-0.5 leading-tight">
                                    <span class="text-sm font-semibold">{{ segment.tag.name || "Unknown" }}</span>
                                    <span v-if="segment.tag.title" class="text-xs text-slate-600">{{ segment.tag.title }}</span>
                                    <span v-if="segment.tag.relationship && segment.tag.relationship !== segment.tag.title" class="text-xs text-slate-500">
                                        {{ segment.tag.relationship }}
                                    </span>
                                </div>
                            </div>
                            <div v-if="segment.tag.was_created" class="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">Newly added to your connections.</div>
                            <a
                                :href="`/people/${segment.tag.connection_id}/`"
                                class="self-start text-xs font-medium text-valence-primary-500 hover:underline"
                                data-testid="user-bubble-tag-open">
                                Open profile →
                            </a>
                        </div>
                    </template>
                </BasePopover>
                <template v-else>{{ segment.text }}</template>
            </template>
        </div>

        <!-- Per-turn role-play coaching note. Only shows when the monitor
             flagged the feedback as significant (the tool drops empty/below-
             threshold notes server-side, so anything we receive here is
             worth rendering). -->
        <div
            v-for="(note, i) in coachingNotes ?? []"
            :key="i"
            :class="[
                'max-w-[80%] rounded-md px-3 py-1.5 text-xs ring-1',
                note.sentiment === 'praise' ? 'bg-emerald-50 text-emerald-900 ring-emerald-200' : 'bg-violet-50 text-violet-900 ring-violet-200',
            ]"
            data-testid="role-play-coaching-note">
            <span :class="['mr-1 text-[10px] font-medium uppercase tracking-wide', note.sentiment === 'praise' ? 'text-emerald-700' : 'text-violet-700']">
                {{ note.sentiment === "praise" ? "✓ Coach" : "↑ Coach" }}
            </span>
            <span class="whitespace-pre-wrap">{{ note.feedback }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import BasePopover from "~vue/components/BasePopover.vue";
import { computed } from "vue";

import type { PersonTag, RolePlayCoachingNoteData, UserMessageData } from "./types";

const props = defineProps<{
    data: UserMessageData;
    // All people-tag spans the agent attached to this user message. Order
    // doesn't matter — we sort by length below to defend against one
    // ``match_text`` being a substring of another (e.g., "Sam" inside "Sam's").
    tags?: PersonTag[];
    // Per-turn role-play coaching notes targeting this user message. The
    // server filters by `is_significant`; whatever lands here is meant to
    // render. Multiple notes can stack if the model produces follow-ups.
    coachingNotes?: RolePlayCoachingNoteData[];
}>();

interface Segment {
    text: string;
    tag: PersonTag | null;
}

// Walk the message left-to-right consuming the longest matching ``match_text``
// at each position. Falls back to a single plain segment when there are no
// tags (or none of them appear in the content). Each tag fires at most once
// per render — repeats stay as plain text, mirroring v1's "tag a name once
// per message" rule.
const segments = computed<Segment[]>(() => {
    const content = props.data.content ?? "";
    const tags = props.tags ?? [];
    if (!content || tags.length === 0) {
        return [{ text: content, tag: null }];
    }

    const sortedTags = [...tags].sort((a, b) => b.match_text.length - a.match_text.length);
    const consumed = new Set<string>();
    const out: Segment[] = [];
    let buffer = "";
    let i = 0;

    while (i < content.length) {
        let matched: PersonTag | null = null;
        for (const tag of sortedTags) {
            const mt = tag.match_text;
            if (!mt) {
                continue;
            }
            const key = `${tag.connection_id}:${mt}`;
            if (consumed.has(key)) {
                continue;
            }
            if (content.substring(i, i + mt.length) === mt) {
                matched = tag;
                consumed.add(key);
                break;
            }
        }
        if (matched) {
            if (buffer) {
                out.push({ text: buffer, tag: null });
                buffer = "";
            }
            out.push({ text: matched.match_text, tag: matched });
            i += matched.match_text.length;
        } else {
            buffer += content[i];
            i += 1;
        }
    }
    if (buffer) {
        out.push({ text: buffer, tag: null });
    }
    return out;
});

const initials = (name: string | null | undefined): string => {
    if (!name) {
        return "?";
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
</script>
